import React, { useState } from 'react'
import styled from 'styled'

import { CategoriesList } from '../../store'

import { ReleaseControlUpdate } from 'domains/release-controls/components/release-control-update'

import { Tour, TourCategory } from 'generated/mos/org/getty/audio'
import { SetReleaseStatusRequest } from 'generated/mos/releasecontrolsmanagement'

import { categorySchema } from '../../schemas/category'

import { Button, ButtonGroup } from 'components/ui/button'
import { Content, Container, Wrap, DragHandle } from 'components/ui/draggable'
import { IconCategory, IconArrow, IconAddItem, IconWarning } from 'components/ui/icons'
import { Title } from 'components/ui/list-item-label'
import { PopOverMenu, PopOverMenuButton } from 'components/ui/pop-over-menu'
import { ReorderableTours } from './reorderable-tours'

import { space } from 'helpers/style'

const Tours = styled.div`
  margin-top: ${space(4)};
  & > *:last-child {
    margin-left: ${space(8)};
  }
`

const Arrow = styled(IconArrow)<{ open: boolean }>`
  transform: rotate(${({ open }) => !open ? '-90' : '0'}deg);
  margin-right: ${space(3)};
`

const Tag = styled(IconCategory)`
  margin-right: ${space(3)};
`

const NoResults = styled.div`
  text-align: center;
  margin-left: ${space(8)};
  padding-top: ${space(5)};
  padding-bottom: ${space(5)};
  border: 2px dashed ${({ theme }) => theme.color.grayL80};
  border-radius: 12px;
`;

type Props = {
  readonly categories: CategoriesList;
  readonly draggableIndex: number | undefined;
  readonly category: TourCategory.Entity;
  readonly deleteHandler: (category: TourCategory.Entity) => void;
  readonly editHandler: (category: TourCategory.Entity) => void;
  readonly updateToursHandler: (category: TourCategory.Entity) => void;
  readonly isDragging: boolean;
  readonly setOrderedCategoryTours: (tours: ReadonlyArray<Tour.Ref>) => void;
  readonly releaseControlUpdate: (bundles: ReadonlyArray<SetReleaseStatusRequest.BundleStatus.Entity>, ref: TourCategory.Ref) => void;
  readonly hasWarning?: boolean;
}

export const CategoryItem = ({ draggableIndex, categories, category, deleteHandler, editHandler, updateToursHandler, isDragging, setOrderedCategoryTours, releaseControlUpdate, hasWarning }: Props) => {
  const [showTours, setShowTours] = useState<boolean>(true)
  const releaseControlUpdateHandler = (bundles: ReadonlyArray<SetReleaseStatusRequest.BundleStatus.Entity>) => {
    releaseControlUpdate(bundles, TourCategory.mustRef(category.ref))
  }
  return (
    <Container isDragging={isDragging}>
      <Wrap isDragging={isDragging}>
        {draggableIndex !== undefined && <DragHandle />}
        <Content onClick={() => setShowTours(!showTours)}>
          <Arrow size="medium" open={showTours} />
          <Tag size="medium" />
          <Title>{categorySchema.name.read(category.name)}</Title>
        </Content>
        <ButtonGroup>
          <ReleaseControlUpdate
            entityRef={category.ref}
            updateHandler={releaseControlUpdateHandler}
          />
          {hasWarning && <IconWarning color="warning" />}
          <PopOverMenu iconSize="base">
            <PopOverMenuButton
              onClick={() => editHandler(category)}
              >
                Edit
            </PopOverMenuButton>
            <PopOverMenuButton
              onClick={() => deleteHandler(category)}
              >
                Delete
            </PopOverMenuButton>
          </PopOverMenu>
        </ButtonGroup>
      </Wrap>
      {showTours && <Tours>
        {category && category.tourRefs && category.tourRefs.length
        ?
          <ReorderableTours
            tours={categories && categories.tours}
            orderedCategoryTours={category.tourRefs}
            setOrderedCategoryTours={setOrderedCategoryTours}
        />
        :
          <NoResults>
            <h3>No Tours</h3>
            <p>Add some tours to this category.</p>
          </NoResults> 
        }
        <Button variant="subtle" onClick={() => updateToursHandler(category)}>
          <IconAddItem size="small" />Add / Remove tours
        </Button>
      </Tours>}
    </Container>
  )
}
